import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Card, Chip } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { DECISIONS } from '@/modules/history/data';
import { useLessons } from '@/modules/history/api';
import { HistoryCard } from '@/modules/history/components/HistoryCard';
import { HistoryDetailModal } from '@/modules/history/components/HistoryDetailModal';
import type { HistoryItem } from '@/modules/history/schemas';

export const Route = createFileRoute('/_authed/history')({
  component: HistoryPage,
});

function HistoryPage() {
  const { data: lessons = [], isLoading: lessonsLoading } = useLessons();
  const [selected, setSelected] = useState<HistoryItem | null>(null);

  const decisionItems: HistoryItem[] = DECISIONS.map((d) => ({ kind: 'decision', ...d }));
  const lessonItems: HistoryItem[] = lessons.map((l) => ({ kind: 'lesson', ...l }));

  return (
    <PageShell
      eyebrow="Histórico"
      title="Decisões & Lições"
      lead="Decisões tomadas e lições aprendidas — fonte de contexto para times e agentes."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Column title="Decisões" tone="info" count={decisionItems.length}>
          {decisionItems.map((item) => (
            <HistoryCard key={item.id} item={item} onSelect={() => setSelected(item)} />
          ))}
        </Column>

        <Column title="Lições" tone="ok" count={lessonItems.length}>
          {lessonsLoading ? (
            <Card padding="lg">
              <p className="text-sm text-text-muted">Carregando lições…</p>
            </Card>
          ) : lessonItems.length === 0 ? (
            <Card padding="lg">
              <p className="text-sm text-text-muted">
                Sem lições registradas em{' '}
                <code className="font-mono text-xs">~/.openclaw/workspace/memory/lessons.md</code>.
              </p>
            </Card>
          ) : (
            lessonItems.map((item) => (
              <HistoryCard key={item.id} item={item} onSelect={() => setSelected(item)} />
            ))
          )}
        </Column>
      </div>

      <HistoryDetailModal item={selected} onClose={() => setSelected(null)} />
    </PageShell>
  );
}

interface ColumnProps {
  title: string;
  tone: 'info' | 'ok';
  count: number;
  children: React.ReactNode;
}

function Column({ title, tone, count, children }: ColumnProps) {
  return (
    <Stack gap="sm">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <span className="size-2 rounded-full" style={{ backgroundColor: tone === 'info' ? 'var(--info)' : 'var(--ok)' }} />
          <span className="text-xs font-semibold uppercase tracking-wider text-text-2">
            {title}
          </span>
        </div>
        <Chip tone="neutral" size="sm">
          {count}
        </Chip>
      </div>
      <Stack gap="sm">{children}</Stack>
    </Stack>
  );
}
